import React, { useState } from 'react';
import { Upload, Image as ImageIcon, Move, Square, Circle, RectangleHorizontal, Minus, Diamond } from 'lucide-react';
import WysiwygEditor from '../WysiwygEditor';

const ArticleEditor = ({ 
  content, 
  onContentChange, 
  uploading, 
  onImageUpload, 
  onOpenImagePicker 
}) => {
  // Initialize content with default values 
  const articleContent = {
    heading: '',
    paragraph: '',
    image: '',
    imageAlt: '',
    imageCredit: null,
    alignment: 'left',
    style: 'fill',
    frame: 'rounded',
    placement: 'left',
    shape: 'none',
    color: 'slate',
    showImage: true,
    ...content
  };

  console.log('ArticleEditor: Current articleContent.paragraph:', articleContent.paragraph);
  console.log('ArticleEditor: Raw content prop:', content);
  console.log('ArticleEditor: All articleContent fields:', articleContent);


  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && onImageUpload) {
      onImageUpload(file, 'image');
    }
  };


  const alignmentOptions = [
    { value: 'left', icon: '📄', label: 'Left' },
    { value: 'center', icon: '📄', label: 'Center' },
    { value: 'right', icon: '📄', label: 'Right' },
    { value: 'justify', icon: '📄', label: 'Justify' }
  ];

  const styleOptions = [
    { value: 'fill', label: 'Fill' },
    { value: 'fit', label: 'Fit' }
  ];

  const frameOptions = [
    { value: 'none', component: Minus },
    { value: 'rounded', component: Diamond },
    { value: 'square', component: Square },
    { value: 'wide', component: RectangleHorizontal },
    { value: 'circle', component: Circle }
  ];

  const placementOptions = [
    { value: 'left', icon: '⬅️', label: 'Left' },
    { value: 'center', icon: '⬆️', label: 'Center' },
    { value: 'right', icon: '➡️', label: 'Right' }
  ];

  const colorOptions = [
    { value: 'slate', color: '#f8fafc', textColor: '#1e293b' },
    { value: 'gray', color: '#f3f4f6', textColor: '#374151' },
    { value: 'blue', color: '#dbeafe', textColor: '#1e40af' },
    { value: 'indigo', color: '#e0e7ff', textColor: '#3730a3' },
    { value: 'purple', color: '#e9d5ff', textColor: '#7c2d12' },
    { value: 'red', color: '#fecaca', textColor: '#dc2626' }
  ];

  return (
    <div className="space-y-6">
      {/* Section Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Heading
        </label>
        <input
          type="text"
          value={articleContent.heading}
          onChange={(e) => onContentChange('heading', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Experience the Charm of Beachside Café in Ankara"
        />
      </div>

      {/* Paragraph Content */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Paragraph
        </label>
        <WysiwygEditor
          value={articleContent.paragraph || ''}
          onChange={(html) => {
            console.log('ArticleEditor: WysiwygEditor onChange called with:', html);
            onContentChange('paragraph', html);
          }}
          placeholder="Write your article content here..."
          className="min-h-[200px]"
        />
        
      </div>

      {/* Text Alignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Alignment
        </label>
        <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          {alignmentOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => onContentChange('alignment', option.value)}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                articleContent.alignment === option.value
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Image Section */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Image
          </label>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Show image</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                type="checkbox" 
                checked={articleContent.showImage}
                onChange={(e) => onContentChange('showImage', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
        </div>

        {articleContent.showImage && (
          <div className="space-y-4">
            {/* Image Upload/Selection */}
            <div className="mb-3">
              <div className="flex gap-2">
                <div 
                  className="flex-1 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                  onClick={() => document.getElementById('articleImageInput').click()}
                >
                  {articleContent.image ? (
                    <img 
                      src={articleContent.image} 
                      alt={articleContent.imageAlt || 'Article image'}
                      className="w-full h-full object-cover rounded"
                    />
                  ) : (
                    <div className="text-center">
                      <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <div className="text-sm text-gray-500">
                        {uploading ? 'Uploading...' : 'Upload'}
                      </div>
                    </div>
                  )}
                  <input
                    id="articleImageInput"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </div>
                {onOpenImagePicker && (
                  <button
                    onClick={() => onOpenImagePicker('image')}
                    className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition min-w-0 flex flex-col items-center justify-center"
                    disabled={uploading}
                  >
                    <ImageIcon className="w-3 h-3 mb-1" />
                    <span>Unsplash</span>
                  </button>
                )}
              </div>
              
              {/* Photo Credit for Article Image */}
              {articleContent.imageCredit && (
                <div className="text-xs text-gray-500 mt-1">
                  Photo by{' '}
                  <a 
                    href={articleContent.imageCredit.photographerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {articleContent.imageCredit.photographer}
                  </a>
                  {' '}on Unsplash
                </div>
              )}
            </div>

            {/* Image Style Options */}
            <div className="grid grid-cols-1 gap-2">
              {/* Style */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Style
                </label>
                <div className="flex space-x-2">
                  {styleOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => onContentChange('style', option.value)}
                      className={`px-3 py-2 border rounded-md text-xs transition ${
                        articleContent.style === option.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Frame */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Frame
                </label>
                <div className="flex space-x-2">
                  {frameOptions.map((option) => {
                    const IconComponent = option.component;
                    return (
                      <button
                        key={option.value}
                        onClick={() => onContentChange('frame', option.value)}
                        className={`p-2 border rounded-md transition ${
                          articleContent.frame === option.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 text-gray-500 hover:border-gray-400'
                        }`}
                      >
                        <IconComponent className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Placement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Placement
              </label>
              <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                {placementOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => onContentChange('placement', option.value)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      articleContent.placement === option.value
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {option.icon} {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Shape */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shape
              </label>
              <select
                value={articleContent.shape}
                onChange={(e) => onContentChange('shape', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="none">None</option>
                <option value="triangle">Triangle</option>
                <option value="diamond">Diamond</option>
                <option value="hexagon">Hexagon</option>
                <option value="star">Star</option>
              </select>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Color
              </label>
              <div className="grid grid-cols-6 gap-2">
                {colorOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => onContentChange('color', option.value)}
                    className={`group relative h-10 rounded-md border-2 transition ${
                      articleContent.color === option.value
                        ? 'border-blue-500 scale-105'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: option.color }}
                  >
                    <div className="flex items-center justify-center h-full">
                      <span 
                        className="text-xs font-medium"
                        style={{ color: option.textColor }}
                      >
                        Aa
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ArticleEditor;